import { Injectable, inject } from '@angular/core';
import { AuthService } from '@features/auth/services/auth.service';
import { TokenManagerService } from './token-manager.service';
import { SessionActivityService } from './session-activity.service';

@Injectable({
  providedIn: 'root'
})
export class AuthDebugService {
  private authService = inject(AuthService);
  private tokenManager = inject(TokenManagerService);
  private sessionActivity = inject(SessionActivityService);

  // ✅ Debug authentication state
  debugAuthState(): void {
    console.group('🔍 Authentication Debug Info');
    
    const token = this.authService.getToken();
    const refreshToken = this.authService.getRefreshToken();
    const user = this.authService.currentUser();
    const isAuthenticated = this.authService.isAuthenticated();
    if (token) {
      this.debugToken(token);
    }
    
    if (refreshToken) {
      this.debugRefreshToken(refreshToken);
    }
    
    this.debugLocalStorage();
    
    console.groupEnd();
  }

  // ✅ Debug token details
  private debugToken(token: string): void {
    try {
      const tokenParts = token.split('.');
      if (tokenParts.length !== 3) {
        console.warn('⚠️ Invalid JWT token format');
        return;
      }

      const header = JSON.parse(atob(tokenParts[0]));
      const payload = JSON.parse(atob(tokenParts[1]));
      
      const isExpired = this.isTokenExpired(payload.exp);
      const expirationDate = new Date(payload.exp * 1000);
      const timeUntilExpiry = expirationDate.getTime() - Date.now();
      if (isExpired) {
        console.error('❌ Access token is expired!');
      } else if (timeUntilExpiry < 5 * 60 * 1000) {
        console.warn('⚠️ Access token expires soon (< 5 minutes)');
      }
      
    } catch (error) {
      console.error('❌ Failed to decode token:', error);
    }
  }

  // ✅ Debug refresh token
  private debugRefreshToken(refreshToken: string): void {
    try {
      const tokenParts = refreshToken.split('.');
      if (tokenParts.length !== 3) {
        console.warn('⚠️ Invalid refresh token format');
        return;
      }

      const payload = JSON.parse(atob(tokenParts[1]));
      const isExpired = this.isTokenExpired(payload.exp);
      const expirationDate = new Date(payload.exp * 1000);
      if (isExpired) {
        console.error('❌ Refresh token is expired!');
      }
      
    } catch (error) {
      console.error('❌ Failed to decode refresh token:', error);
    }
  }

  // ✅ Debug localStorage
  private debugLocalStorage(): void {
    const storageItems = {
      token: localStorage.getItem('token'),
      refreshToken: localStorage.getItem('refreshToken'),
      user: localStorage.getItem('user'),
      pendingVerificationEmail: localStorage.getItem('pendingVerificationEmail')
    };
    if (storageItems.user) {
      try {
        const userData = JSON.parse(storageItems.user);
      } catch (error) {
        console.error('❌ Failed to parse stored user data:', error);
      }
    }
  }

  // ✅ Check if token is expired
  private isTokenExpired(exp: number): boolean {
    if (!exp) return true;
    return Date.now() >= exp * 1000;
  }

  // ✅ Simulate token refresh
  async testTokenRefresh(): Promise<void> {
    console.group('🔄 Testing Token Refresh');
    
    try {
      const newToken = await this.authService.refreshAccessToken().toPromise();
    } catch (error) {
      console.error('❌ Token refresh failed:', error);
    }
    
    console.groupEnd();
  }

  // ✅ Debug token manager status
  debugTokenManager(): void {
    console.group('🔄 Token Manager Debug Info');
    
    const status = this.tokenManager.getRefreshStatus();
    console.groupEnd();
  }

  // ✅ Test automatic token refresh
  async testAutoRefresh(): Promise<void> {
    console.group('⚡ Testing Auto Token Refresh');
    
    try {
      const result = await this.tokenManager.manualRefresh().toPromise();
    } catch (error) {
      console.error('❌ Manual refresh test failed:', error);
    }
    
    console.groupEnd();
  }

  // ✅ Force session extension
  async extendSession(): Promise<void> {
    console.group('⏰ Extending Session');
    
    try {
      const result = await this.tokenManager.extendSession().toPromise();
    } catch (error) {
      console.error('❌ Session extension failed:', error);
    }
    
    console.groupEnd();
  }

  // ✅ Reset token manager state
  resetTokenManager(): void {
    this.tokenManager.resetRefreshAttempts();
  }

  // ✅ Debug session activity
  debugSessionActivity(): void {
    console.group('🎯 Session Activity Debug Info');
    
    const activityInfo = this.sessionActivity.getActivityInfo();
    const sessionWarning = this.sessionActivity.getSessionWarning();
    const shouldWarn = this.sessionActivity.shouldWarnUser();
    console.groupEnd();
  }

  // ✅ Test session extension
  async testSessionExtension(): Promise<void> {
    console.group('⏰ Testing Session Extension');
    
    try {
      const result = await this.sessionActivity.extendSessionManually().toPromise();
    } catch (error) {
      console.error('❌ Session extension failed:', error);
    }
    
    console.groupEnd();
  }

  // ✅ Clear all auth data (for testing)
  clearAuthData(): void {
    console.warn('🧹 Clearing all authentication data...');
    
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    localStorage.removeItem('pendingVerificationEmail');
  }
} 